import cn from 'classnames';
import { css, html, type PropertyValues } from 'lit';

import { GamAdVendor } from '@client/adManager/GamAdVendor.js';
import { AfsAdVendor } from '@client/adsense/AfsAdVendor.js';
import { AdVendor } from '@client/core/AdVendor.js';
import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { $placementsMap } from '@client/core/atoms/placements.js';
import { EmptyAdVendor } from '@client/core/EmptyAdVendor.js';
import { listenKeys } from '@podium/store';
import { i18n } from '@lingui/core';
import {
  AdPlacement,
  AdServer,
  debugLog,
  type LayoutType,
  PageType,
  type PlacementId,
  PlacementStatus,
  type RecommendationCategories
} from '@schibsted-nmp/advertising-shared';
import WarpElement from '@warp-ds/elements-core';
import { WallpaperHandler } from '@client/core/utils/dom/WallpaperHandler.js';

import AdvtComponentStyles, {
  getClassNameByStatus,
  getClassNameByType
} from './AdvtComponentStyles.js';

const NAME = 'advt-component';

type Props = {
  placementId: PlacementId | null;
  containerId: string;
  adIndex?: number;
  categories?: RecommendationCategories;
};

class AdvtComponent extends WarpElement implements Props {
  // Properties with default values or required indicators
  containerId: string;

  placementId: PlacementId | null;

  adIndex?: number;

  categories?: RecommendationCategories;

  initialLayoutType?: LayoutType;

  adVendorInstance?: InstanceType<typeof AdVendor>;

  private _adTypeClassName: string;

  private _originalClassName: string;

  private _statusClassName: string;

  private _typeClassName: string;

  private unlistenPlacements: (() => void) | null = null;

  static override get properties() {
    return {
      containerId: { type: String },
      placementId: { type: String },
      initialLayoutType: { type: String },
      adIndex: { type: Number },
      categories: { type: Object }
    };
  }

  constructor() {
    super();
    this.placementId = null;
    this.containerId = ``;
    this.adIndex = 0;
    this._adTypeClassName = '';
    this._originalClassName = this.className;
    this._statusClassName = '';
    this._typeClassName = '';
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.adVendorInstance?.cleanupEvents();

    this.unlistenPlacements?.();
    this.unlistenPlacements = null;

    // safeguarding with AdvtInCarousel, but this should be used for all placements, for propper cleanup
    if (
      this.placementId === AdPlacement.AdvtInCarousel.id &&
      AdvtComponent.instanceRegistry.get(this.placementId) === this
    ) {
      AdvtComponent.instanceRegistry.delete(this.placementId);
    }
  }

  private static instanceRegistry = new Map<string, AdvtComponent>();

  override connectedCallback(): void {
    // TODO: This can be removed when item-page fixes their recommendation re-render stuff
    const pageType = $config.get()?.pageType;
    const { placementId } = this;

    if (!placementId) {
      console.warn(`AdvtComponent: No placementId provided. Skipping render.`);
      return;
    }

    if (
      pageType === PageType.Object &&
      AdvtComponent.instanceRegistry.has(placementId)
    ) {
      console.warn(
        `AdvtComponent with placementId: ${placementId} already exists. Skipping render.`
      );

      return;
    }

    AdvtComponent.instanceRegistry.set(placementId, this);

    super.connectedCallback();
    this.initAdVendorInstance();

    const IGNORED_PLACEMENT_STATUSES = [
      'request',
      'requested',
      'pending',
      'forceBatchRequest'
    ];

    updateMetricByKey(placementId, PlacementStatus.ElementInDom);

    // 🔒 ensure we don't double-subscribe if connectedCallback runs again
    this.unlistenPlacements?.();

    this.unlistenPlacements = listenKeys(
      $placementsMap,
      [`${placementId}.status`, `${placementId}.creativeType`],
      ({ [placementId]: placement }, { [placementId]: oldPlacement }) => {
        if (!placement) return;

        const { status, creativeType } = placement;

        if (
          oldPlacement?.status === status &&
          oldPlacement.creativeType === creativeType
        ) {
          /*
            Since we subscribe to changes for any placement in $placementsMap, this
            callback will be triggered multiple times for the same placement with
            the same values. Thus we need to eject when neither `status` nor
            `creativeType` have actually changed.
          */
          return;
        }

        debugLog(
          `AdvtComponent: ${placementId} status/creativeType changed: ${status}/${creativeType ?? 'unknown'}`
        );

        if (
          status === 'forceBatchRequest' &&
          placementId === AdPlacement.AdvtWallpaper.id
        ) {
          WallpaperHandler.cleanup();
        }

        if (IGNORED_PLACEMENT_STATUSES.includes(status)) return;

        if (creativeType) {
          this._typeClassName = getClassNameByType(creativeType);
        }

        this._statusClassName = getClassNameByStatus(status);

        this.className = cn(
          this._originalClassName,
          this._adTypeClassName,
          this._statusClassName,
          this._typeClassName
        );
      }
    );

    this.adVendorInstance?.setupEvents();
  }

  private initAdVendorInstance() {
    if (!this.placementId) {
      debugLog(`initAdVendorInstance failed. No placementId provided.`);
      return;
    }

    const placement = $placementsMap.get()[this.placementId];
    const { pageType, brand, vertical } = $config.get() ?? {};
    const dataClassName = `${pageType}-page ${brand}-brand ${vertical}-vertical`;

    // Set a localized advertisement title and assign role="img" to improve accessibility for banner content
    this.title = i18n.t({
      id: 'native.advertisement',
      message: 'advertisement'
    });

    this.role = 'img';

    if (!placement) {
      debugLog(
        `initAdVendorInstance failed. Missing placement '${this.placementId}' from config`
      );

      this.adVendorInstance = new EmptyAdVendor(this.placementId);
      this.className = 'advt-empty';

      return;
    }

    if (placement.adServer.type === AdServer.Afs) {
      this.adVendorInstance = new AfsAdVendor(this.placementId);
    } else {
      this.adVendorInstance = new GamAdVendor(this.placementId);
    }

    this._adTypeClassName = `advt-g ${dataClassName}`;
    this.className = cn(
      this._adTypeClassName,
      // Adding statusClassNme and typeClassName because of AD-270 - see explanation in ticket.
      this._statusClassName,
      this._typeClassName
    );

    debugLog(
      `AdvtComponent: Using ${this.adVendorInstance.adServer ?? 'unset ad server'} for ${this.placementId}`
    );
  }

  private makeSlotAvailableFromLightDOM(placementId: PlacementId) {
    const containerId =
      this.adVendorInstance?.adServer === AdServer.Afs
        ? placementId
        : `${placementId}--container`;

    if (this.querySelector(`#${containerId}`)) {
      // If the container is already in the surrounding DOM, do nothing. This
      // check makes the component backwards compatible when the slotted
      // container is added by the component user.
      //
      // This is necessary because the component is used in two ways:
      // 1. The component user adds the container to the light DOM. This is the
      //    recommended way of using the component. One reason for this is to avoid
      //    hydration mismatch issues when using the component with React SSR.
      // 2. The component user does not add the container to the light DOM.
      //    This is the legacy way of using the component. The component adds the
      //    container to the light DOM itself, in order to be backwards compatible.
      return;
    }

    const slotDiv = document.createElement('div');
    slotDiv.slot = `${placementId}--slot`;
    slotDiv.id = containerId;

    // add the div with slot available in the light DOM
    this.appendChild(slotDiv);
  }

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    if (this.adVendorInstance?.placementId) {
      this.makeSlotAvailableFromLightDOM(this.adVendorInstance.placementId);
    }

    if (this.adVendorInstance?.adServer === AdServer.Gam) {
      this.adVendorInstance.requestAd();
    }
  }

  static override styles = [
    AdvtComponentStyles,
    css`
      *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.badge--info{background-color:var(--w-color-badge-info-background);color:var(--w-s-color-text)}.badge--negative{background-color:var(--w-color-badge-negative-background);color:var(--w-s-color-text)}.badge--neutral{background-color:var(--w-color-badge-neutral-background);color:var(--w-s-color-text)}.badge--positive{background-color:var(--w-color-badge-positive-background);color:var(--w-s-color-text)}.badge--warning{background-color:var(--w-color-badge-warning-background);color:var(--w-s-color-text)}.badge{font-size:var(--w-font-size-xs);line-height:var(--w-line-height-xs);border-width:0;border-radius:4px;padding:.4rem .8rem;display:inline-flex}.badge--positionTL{font-size:var(--w-font-size-xs);line-height:var(--w-line-height-xs);border-width:0;border-radius:0 0 4px;padding:.4rem .8rem;display:inline-flex;position:absolute;top:0;left:0}.card--cardShadow{background-color:var(--w-s-color-surface-elevated-200);box-shadow:var(--w-s-shadow-surface-elevated-200);border-radius:8px}.card{cursor:pointer;transition-property:all;transition-duration:.15s;transition-timing-function:cubic-bezier(.4,0,.2,1);position:relative;overflow:hidden}.page-container{background-color:var(--w-s-color-background);max-width:1010px;margin:0;padding:0 16px}.card--cardShadow:hover{background-color:var(--w-s-color-surface-elevated-200-hover);box-shadow:var(--w-s-shadow-surface-elevated-200-hover)}.card--cardShadow:active{background-color:var(--w-s-color-surface-elevated-200-active);box-shadow:var(--w-s-shadow-surface-elevated-200-active)}.h2{font-weight:700;font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}.h4{font-weight:700;font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}.h5{font-weight:700;font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}.text-caption{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}.text-detail{font-size:var(--w-font-size-xs);line-height:var(--w-line-height-xs)}@media (min-width:1300px){.page-container{margin-left:auto;margin-right:auto;padding-left:31px;padding-right:31px}}.text-center{text-align:center}.align-start{vertical-align:top}.aspect-1\/1{padding-bottom:100%;position:relative}.aspect-1\/1>*{width:100%;height:100%;position:absolute;inset:0}.aspect-16\/9{padding-bottom:56.25%;position:relative}.aspect-16\/9>*{width:100%;height:100%;position:absolute;inset:0}.aspect-3\/2{padding-bottom:66.6667%;position:relative}.aspect-3\/2>*{width:100%;height:100%;position:absolute;inset:0}.aspect-3\/4{padding-bottom:133.333%;position:relative}.aspect-3\/4>*{width:100%;height:100%;position:absolute;inset:0}.aspect-4\/3{padding-bottom:75%;position:relative}.aspect-4\/3>*{width:100%;height:100%;position:absolute;inset:0}.bg-green-100,.bg-red-100{background-position:undefined undefined}.bg-transparent{background-color:#0000}.bg-\[--w-aqua-200\]{background-color:var(--w-aqua-200)}.bg-\[--w-aqua-50\]{background-color:var(--w-aqua-50)}.bg-\[--w-green-100\]{background-color:var(--w-green-100)}.bg-\[--w-red-100\]{background-color:var(--w-red-100)}.bg-\[--w-yellow-100\]{background-color:var(--w-yellow-100)}.border{border-width:1px}.border-2{border-width:2px}.border-transparent{border-color:#0000}.border-\[--w-aqua-200\]{border-color:var(--w-aqua-200)}.border-\[--w-aqua-50\]{border-color:var(--w-aqua-50)}.border-\[--w-green-100\]{border-color:var(--w-green-100)}.border-\[--w-red-100\]{border-color:var(--w-red-100)}.border-\[--w-yellow-100\]{border-color:var(--w-yellow-100)}.rounded,.rounded-4{border-radius:4px}.rounded-0{border-radius:0}.rounded-8{border-radius:8px}.rounded-full{border-radius:9999px}.rounded-b-8{border-bottom-right-radius:8px;border-bottom-left-radius:8px}.text-\[--w-aqua-800\]{color:var(--w-aqua-800)}.text-\[--w-green-800\]{color:var(--w-green-800)}.text-\[--w-red-800\]{color:var(--w-red-800)}.text-\[--w-yellow-800\]{color:var(--w-yellow-800)}.block{display:block}.inline-block{display:inline-block}.flex{display:flex}.inline-flex{display:inline-flex}.grid{display:grid}.hidden{display:none}.underline{text-decoration-line:underline}.hover\:no-underline:hover{text-decoration:none}.shrink-0{flex-shrink:0}.flex-row{flex-direction:row}.flex-col{flex-direction:column}.flex-wrap{flex-wrap:wrap}.gap-0{gap:0}.gap-1{gap:.1rem}.gap-16{gap:1.6rem}.gap-2{gap:.2rem}.gap-4{gap:.4rem}.gap-8{gap:.8rem}.gap-x-16{column-gap:1.6rem}.gap-x-8{column-gap:.8rem}.col-span-2{grid-column:span 2/span 2}.col-span-3{grid-column:span 3/span 3}.grid-rows-1{grid-template-rows:repeat(1,minmax(0,1fr))}.grid-rows-2{grid-template-rows:repeat(2,minmax(0,1fr))}.grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.overflow-auto{overflow:auto}.overflow-hidden{overflow:hidden}.overflow-y-auto{overflow-y:auto}.overflow-y-scroll{overflow-y:scroll}.line-clamp-1{-webkit-line-clamp:1;line-clamp:1;-webkit-box-orient:vertical;display:-webkit-box;overflow:hidden}.line-clamp-2{-webkit-line-clamp:2;line-clamp:2;-webkit-box-orient:vertical;display:-webkit-box;overflow:hidden}.list-decimal{list-style-type:decimal}.outline-none{outline-offset:2px;outline:2px solid #0000}.items-center{align-items:center}.self-center{align-self:center}.inset-0{inset:0}.bottom-0{bottom:0}.left-0{left:0}.left-10{left:1rem}.right-0{right:0}.right-14{right:1.4rem}.right-6{right:.6rem}.top-0{top:0}.top-10{top:1rem}.top-14{top:1.4rem}.top-6{top:.6rem}.justify-end{justify-content:flex-end}.justify-center{justify-content:center}.justify-between{justify-content:space-between}.justify-self-start{justify-self:start}.order-first{order:-9999}.order-last{order:9999}.absolute{position:absolute}.fixed{position:fixed}.relative{position:relative}.static{position:static}.sticky{position:sticky}.z-30{z-index:30}.z-50{z-index:50}.s-bg{background-color:var(--w-s-color-background)}.s-bg-hover{background-color:var(--w-s-color-background-hover)}.s-bg-info-subtle{background-color:var(--w-s-color-background-info-subtle)}.s-bg-negative-subtle{background-color:var(--w-s-color-background-negative-subtle)}.s-bg-positive-subtle{background-color:var(--w-s-color-background-positive-subtle)}.s-bg-primary{background-color:var(--w-s-color-background-primary)}.s-bg-selected{background-color:var(--w-s-color-background-selected)}.s-bg-subtle{background-color:var(--w-s-color-background-subtle)}.s-bg-warning-subtle{background-color:var(--w-s-color-background-warning-subtle)}.hover\:s-bg-info-subtle:hover{background-color:var(--w-s-color-background-info-subtle)}.hover\:s-bg-subtle:hover{background-color:var(--w-s-color-background-subtle)}.focus\:s-bg-info-subtle:focus{background-color:var(--w-s-color-background-info-subtle)}.s-text{color:var(--w-s-color-text)}.s-text-info{color:var(--w-s-color-text-info)}.s-text-inverted{color:var(--w-s-color-text-inverted)}.s-text-link{color:var(--w-s-color-text-link)}.s-text-negative{color:var(--w-s-color-text-negative)}.s-text-positive{color:var(--w-s-color-text-positive)}.s-text-subtle{color:var(--w-s-color-text-subtle)}.s-text-warning{color:var(--w-s-color-text-warning)}.s-text\!{color:var(--w-s-color-text)!important}.s-icon-subtle{color:var(--w-s-color-icon-subtle)}.s-border{border-color:var(--w-s-color-border)}.s-border-info{border-color:var(--w-s-color-border-info)}.s-border-negative{border-color:var(--w-s-color-border-negative)}.s-border-positive{border-color:var(--w-s-color-border-positive)}.s-border-warning{border-color:var(--w-s-color-border-warning)}.shadow-m{box-shadow:var(--w-shadow-m)}.shadow-s{box-shadow:var(--w-shadow-s)}.hover\:shadow-m:hover{box-shadow:var(--w-shadow-m)}.h-20{height:2rem}.h-28{height:2.8rem}.h-32{height:3.2rem}.h-full{height:100%}.max-h-24{max-height:2.4rem}.max-h-screen{max-height:100vh}.max-w-screen{max-width:100vw}.min-h-28{min-height:2.8rem}.min-w-96{min-width:9.6rem}.w-0{width:0}.w-20{width:2rem}.w-96{width:9.6rem}.w-fit{width:fit-content}.w-full{width:100%}.h-\[46\]{height:4.6rem}.min-h-\[36\]{min-height:3.6rem}.w-\[74\]{width:7.4rem}.space-x-12>:not([hidden])~:not([hidden]){--w-space-x-reverse:0;margin-left:calc(1.2rem*calc(1 - var(--w-space-x-reverse)));margin-right:calc(1.2rem*var(--w-space-x-reverse))}.space-x-16>:not([hidden])~:not([hidden]){--w-space-x-reverse:0;margin-left:calc(1.6rem*calc(1 - var(--w-space-x-reverse)));margin-right:calc(1.6rem*var(--w-space-x-reverse))}.space-y-2>:not([hidden])~:not([hidden]){--w-space-y-reverse:0;margin-top:calc(.2rem*calc(1 - var(--w-space-y-reverse)));margin-bottom:calc(.2rem*var(--w-space-y-reverse))}.m-16{margin:1.6rem}.m-8{margin:.8rem}.m-auto{margin:auto}.mx-16{margin-left:1.6rem;margin-right:1.6rem}.mx-auto{margin-left:auto;margin-right:auto}.my-2{margin-top:.2rem;margin-bottom:.2rem}.mb-0{margin-bottom:0}.mb-16{margin-bottom:1.6rem}.mb-2{margin-bottom:.2rem}.mb-4{margin-bottom:.4rem}.mb-8{margin-bottom:.8rem}.ml-16{margin-left:1.6rem}.ml-4{margin-left:.4rem}.ml-8{margin-left:.8rem}.mr-16{margin-right:1.6rem}.mr-32{margin-right:3.2rem}.mr-4{margin-right:.4rem}.mr-8{margin-right:.8rem}.mt-12{margin-top:1.2rem}.mt-16{margin-top:1.6rem}.mt-4{margin-top:.4rem}.mt-8{margin-top:.8rem}.p-0{padding:0}.p-10{padding:1rem}.p-16{padding:1.6rem}.p-2{padding:.2rem}.p-24{padding:2.4rem}.p-4{padding:.4rem}.p-8{padding:.8rem}.px-2{padding-left:.2rem;padding-right:.2rem}.px-8{padding-left:.8rem;padding-right:.8rem}.py-1{padding-top:.1rem;padding-bottom:.1rem}.py-2{padding-top:.2rem;padding-bottom:.2rem}.py-4{padding-top:.4rem;padding-bottom:.4rem}.py-8{padding-top:.8rem;padding-bottom:.8rem}.pl-8{padding-left:.8rem}.pr-44{padding-right:4.4rem}.pr-8{padding-right:.8rem}.visible{visibility:visible}.break-words{overflow-wrap:break-word}.break-all{word-break:break-all}.cursor-pointer{cursor:pointer}.italic{font-style:italic}.font-bold{font-weight:700}.font-normal{font-weight:400}.object-cover{object-fit:cover}.object-contain{object-fit:contain}.object-none{object-fit:none}.object-center{object-position:center}.pointer-events-none{pointer-events:none}.resize{resize:both}.truncate{text-overflow:ellipsis;white-space:nowrap;overflow:hidden}.text-ellipsis{text-overflow:ellipsis}.whitespace-nowrap{white-space:nowrap}.transform{transform:translateX(var(--w-translate-x))translateY(var(--w-translate-y))translateZ(var(--w-translate-z))rotate(var(--w-rotate))rotateX(var(--w-rotate-x))rotateY(var(--w-rotate-y))rotateZ(var(--w-rotate-z))skewX(var(--w-skew-x))skewY(var(--w-skew-y))scaleX(var(--w-scale-x))scaleY(var(--w-scale-y))scaleZ(var(--w-scale-z))}.transition{transition-property:color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter,backdrop-filter;transition-duration:.15s;transition-timing-function:cubic-bezier(.4,0,.2,1)}.transition-all{transition-property:all;transition-duration:.15s;transition-timing-function:cubic-bezier(.4,0,.2,1)}.duration-500{transition-duration:.5s}.ease-in-out{transition-timing-function:cubic-bezier(.4,0,.2,1)}.text-12,.text-xs{font-size:var(--w-font-size-xs);line-height:var(--w-line-height-xs)}.text-14,.text-s{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}.text-m{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}@media (min-width:480px){.sm\:text-right{text-align:right}.sm\:border-0{border-width:0}.sm\:rounded-0{border-radius:0}.sm\:rounded-tl-0{border-top-left-radius:0}.sm\:block{display:block}.sm\:inline{display:inline}.sm\:flex{display:flex}.sm\:gap-x-16{column-gap:1.6rem}.sm\:gap-y-4{row-gap:.4rem}.sm\:row-span-2{grid-row:span 2/span 2}.sm\:col-span-1{grid-column:span 1/span 1}.sm\:col-span-2{grid-column:span 2/span 2}.sm\:col-span-3{grid-column:span 3/span 3}.sm\:row-start-1{grid-row-start:1}.sm\:col-start-3{grid-column-start:3}.sm\:grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.sm\:grid-cols-4{grid-template-columns:repeat(4,minmax(0,1fr))}.sm\:items-center{align-items:center}.sm\:items-baseline{align-items:baseline}.sm\:inset-0{inset:0}.sm\:justify-between{justify-content:space-between}.sm\:order-first{order:-9999}.sm\:absolute{position:absolute}.sm\:relative{position:relative}.sm\:max-h-40{max-height:4rem}.sm\:w-96{width:9.6rem}.sm\:h-\[36\]{height:3.6rem}.sm\:h-\[68\]{height:6.8rem}.sm\:m-0{margin:0}.sm\:mb-16{margin-bottom:1.6rem}.sm\:ml-16{margin-left:1.6rem}.sm\:mr-16{margin-right:1.6rem}.sm\:mt-0{margin-top:0}.sm\:mt-24{margin-top:2.4rem}.sm\:mt-4{margin-top:.4rem}.sm\:mt-8{margin-top:.8rem}.sm\:p-0{padding:0}.sm\:pb-16{padding-bottom:1.6rem}.sm\:pr-16{padding-right:1.6rem}.sm\:pr-44{padding-right:4.4rem}}@media (min-width:768px){.md\:my-6{margin-top:.6rem;margin-bottom:.6rem}.md\:mt-8{margin-top:.8rem}}.sf-primary-tag{position:absolute;top:8px;left:8px}.sf-primary-tag-legendary{position:absolute;top:10px;left:10px}@supports selector(:has(a,b)){.sf-search-ad:has(.sf-search-ad-link:focus-visible)>.sf-search-ad-outline{border-color:var(--w-s-color-border-selected)}.sf-search-ad-legendary:has(.sf-search-ad-link:focus-visible)>.sf-search-ad-outline{border-color:var(--w-s-color-border-info-subtle)}.sf-search-ad-link:focus{-webkit-text-decoration:none;text-decoration:none}.sf-search-ad-link:focus-visible{outline:none}.sf-search-ad{transition-property:background-color,border-color,box-shadow}}.sf-search-ad-legendary{border-radius:12px}.sf-search-ad-legendary:focus-within{background-color:var(--w-s-color-background-info-subtle)}.sf-search-ad-legendary:focus-within>.sf-search-ad-outline{border-color:var(--w-s-color-border-info-subtle);border-radius:12px}.sf-realestate-heading{-webkit-line-clamp:2;-webkit-box-orient:vertical;display:-webkit-box;overflow:hidden}@media (min-width:480px){.sf-realestate-heading{-webkit-line-clamp:1}.sf-realestate-image{min-height:160px}.sf-realestate-location{max-width:300px}.sf-realestate-image-aspect-none{padding-bottom:0!important;position:static!important}.sf-realestate-image-aspect-none>*{width:100%;height:100%;position:absolute;inset:0}}.sf-line-clamp-1,.sf-line-clamp-2,.sf-line-clamp-3{-webkit-box-orient:vertical;display:-webkit-box;overflow:hidden}.sf-line-clamp-1{-webkit-line-clamp:1}.sf-line-clamp-2{-webkit-line-clamp:2}.sf-line-clamp-3{-webkit-line-clamp:3};
    `,
    ...WarpElement.styles
  ];

  override render() {
    if (this.adVendorInstance) {
      return this.adVendorInstance.render();
    }
    return html``;
  }
}
export function defineAdvtComponent() {
  if (!customElements.get(NAME)) {
    customElements.define(NAME, AdvtComponent);
  }
}
